import { Link } from "react-router-dom";
import { Map as MapIcon } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { useMpzpCandidates } from "../../hooks";

/**
 * Rada w projekcie ZE: które działki TEGO projektu wymagają wypisu i wyrysu z MPZP.
 *
 * Stoi na zakładce Działki, bo tam siedzą dane, z których liczą się reguły (kategoria
 * lokalizacji, klasoużytek, formy ochrony) — i tam użytkownik je ogląda. Panel niczego
 * nie zamawia: prowadzi do modułu, gdzie zamówienie składa się per gmina, bo gmina
 * zwykle obejmuje działki z kilku projektów naraz.
 *
 * Milczy, gdy nie ma czego doradzić — pusty pasek na każdej zakładce uczy ignorowania.
 */
export function MpzpAdvicePanel({ connectionId }: { connectionId: string }) {
  const { data } = useMpzpCandidates({ connection: connectionId });
  const groups = data?.groups ?? [];

  const required = groups.flatMap((g) => g.parcels.filter((p) => p.reasons.length > 0));
  if (!required.length) return null;

  const byReason = new Map<string, number>();
  required.forEach((p) =>
    p.reasons.forEach((r) => byReason.set(r.label, (byReason.get(r.label) ?? 0) + 1)),
  );
  const preselected = required.filter((p) => p.preselected).length;
  const communes = Array.from(new Set(required.map((p) => p.commune).filter(Boolean)));

  return (
    <div className="mb-3 border border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)] p-2.5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <div className="text-sm font-medium flex items-center gap-2">
            <MapIcon size={14} className="shrink-0" />
            Wypis i wyrys z MPZP — {required.length} działek tego projektu wymaga zamówienia
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Array.from(byReason.entries()).map(([label, count]) => (
              <Badge key={label} color="blue">
                {label}: {count}
              </Badge>
            ))}
          </div>
          <p className="text-[11px] text-[var(--color-text-muted)]">
            {preselected === required.length ? (
              <>Wszystkie są gotowe do zamówienia.</>
            ) : (
              <>
                {preselected} z {required.length} ma potwierdzone pokrycie planem miejscowym —
                reszta wymaga sprawdzenia albo świadomej decyzji (gmina wyda wtedy zaświadczenie
                o braku planu).
              </>
            )}{" "}
            Zamówienie składa się per gmina
            {communes.length > 1 ? ` (tu: ${communes.length})` : ""}.
          </p>
        </div>
        <Link
          to={`/power-projects/zamawianie?tab=mpzp&connection=${connectionId}`}
          className="shrink-0 inline-flex items-center gap-1 text-xs font-medium border border-[var(--color-border-strong)] px-2.5 py-1.5 hover:bg-[var(--color-bg-hover)]"
        >
          Przejdź do zamawiania →
        </Link>
      </div>
    </div>
  );
}
